import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { Carousel } from 'dibs-carousel';
import { Swiper } from 'dibs-pinch-zoom-swipe';

import { HpRespModuleContainerOld } from '../HpRespModuleContainerOld/HpRespModuleContainerOld';
import { SharedCarouselDot } from '../../sharedComponents/SharedCarouselDot/SharedCarouselDot';
import { HpRespHeroModuleItemOld } from './HpRespHeroModuleItemOld';

import { RenderIn, device } from '../../utils/RenderIn';
import { HpRespCarouselTracking } from '../HpRespCarouselTracking/HpRespCarouselTracking';
import { trackHeroImpression, trackHeroItemClick } from './helpers/HeroTrackingOld';
import { SharedInViewport } from '../../sharedComponents/SharedInViewport';
import { TABLET_ITEMS_PER_PAGE } from '../constants';
import styles from './HpRespHeroModuleOld.scss';

/**
 *  Removes items residue because we want to make sure that all of the carousel pages are full
 */
function removeItemsResidue({ totalItems, itemsPerPage }) {
    const residue = totalItems % itemsPerPage;

    if (residue !== 0) {
        return totalItems - residue;
    }

    return totalItems;
}

function HpRespHeroModuleOldComponent({ componentModule, useLazyLoadImages }) {
    if (!componentModule) {
        return null;
    }

    const { items, title } = componentModule;

    return (
        <HpRespModuleContainerOld addTopGap={!title}>
            <HpRespModuleContainerOld.Header>{title}</HpRespModuleContainerOld.Header>
            <RenderIn deviceTypes={[device.DESKTOP, device.TABLET]}>
                {({ currentDevice }) => {
                    const itemsPerPage =
                        currentDevice === device.DESKTOP ? TABLET_ITEMS_PER_PAGE : 3;
                    const totalItems = removeItemsResidue({
                        totalItems: items.length,
                        itemsPerPage,
                    });
                    return (
                        <HpRespCarouselTracking
                            items={items}
                            itemsPerPage={itemsPerPage}
                            onItemsImpression={({ visibleItems, index }) =>
                                trackHeroImpression({
                                    items: visibleItems,
                                    startIndex: index,
                                })
                            }
                        >
                            {({ handleIndexChange }) => (
                                <SharedInViewport>
                                    {({ inViewport }) => (
                                        <Carousel
                                            classNames={{ listWrapper: styles.listWrapper }}
                                            isAutoRun={inViewport && totalItems > itemsPerPage}
                                            dataTn="hero-items-carousel"
                                            showDots={totalItems > itemsPerPage}
                                            hideArrows
                                            totalItems={totalItems}
                                            itemsPerPage={itemsPerPage}
                                            step={itemsPerPage}
                                            renderItem={({ index, isVisible }) => (
                                                <HpRespHeroModuleItemOld
                                                    item={items[index]}
                                                    isVisible={isVisible}
                                                    useLazyLoadImages={useLazyLoadImages}
                                                    onClick={() =>
                                                        trackHeroItemClick({
                                                            item: items[index],
                                                            index,
                                                        })
                                                    }
                                                />
                                            )}
                                            onIndexChange={handleIndexChange}
                                            renderDot={({ isCurrentDot } = {}) => (
                                                <SharedCarouselDot isActive={isCurrentDot} />
                                            )}
                                        />
                                    )}
                                </SharedInViewport>
                            )}
                        </HpRespCarouselTracking>
                    );
                }}
            </RenderIn>

            <RenderIn deviceTypes={[device.MOBILE]}>
                {() => {
                    const itemsPerPage = 1.5;

                    return (
                        <HpRespCarouselTracking
                            items={items}
                            itemsPerPage={itemsPerPage}
                            onItemsImpression={({ visibleItems, index }) =>
                                trackHeroImpression({
                                    items: visibleItems,
                                    startIndex: index,
                                })
                            }
                        >
                            {({ handlePageChange }) => (
                                <Swiper itemsPerPage={itemsPerPage} onPageChange={handlePageChange}>
                                    {items.map((item, index) => (
                                        <HpRespHeroModuleItemOld
                                            key={item.cmsId}
                                            item={item}
                                            useLazyLoadImages={useLazyLoadImages}
                                            onClick={() => trackHeroItemClick({ item, index })}
                                        />
                                    ))}
                                </Swiper>
                            )}
                        </HpRespCarouselTracking>
                    );
                }}
            </RenderIn>
        </HpRespModuleContainerOld>
    );
}

HpRespHeroModuleOldComponent.propTypes = {
    componentModule: PropTypes.object.isRequired,
    useLazyLoadImages: PropTypes.bool,
};

export const HpRespHeroModuleOld = createFragmentContainer(HpRespHeroModuleOldComponent, {
    componentModule: graphql`
        fragment HpRespHeroModuleOld_componentModule on HeroModule {
            title
            items {
                cmsId
                link
                imageUrl
                title
                contemporaryTrackingString
            }
        }
    `,
});
